import { useState, type PointerEvent } from 'react'
import { niceMax, ticks } from '../../lib/chartGeometry'
import { fmtInt } from '../../lib/format'
import { bandPath, linePath, markerX, splitIncomplete, xForDay } from '../../lib/trendGeometry'

export interface TrendSeriesLine {
  label: string
  values: (number | null)[]
  emphasis?: boolean
  dashed?: boolean
}

type Props = {
  series: TrendSeriesLine[]
  labels: string[]
  band?: { low: (number | null)[]; high: (number | null)[] }
  /** Index of the first unfinished day: that tail is drawn dotted. */
  incompleteFrom?: number
  markers?: { index: number; label: string }[]
  height?: number
  ariaLabel: string
  /** Formats a value for the axis, tooltip and table. Defaults to a thousands-separated count. */
  format?: (v: number) => string
}

const W = 760
const LEFT = 48
const RIGHT = 10
const TOP = 14
const BOTTOM = 22
const ACCENT = '#ffb648'
const INK = ['#b7b1a3', '#837e73', '#5d5a52']   // neutral ink for the non-emphasis series
const GRID = '#1f2530'
const TEXT = '#b7b1a3'

/** One-axis line chart for the long view. The emphasis series takes the accent,
 *  others neutral ink. The unfinished tail is dotted, an optional band shows
 *  the normal range, and hovering shows every series on the nearest day. */
export function TrendChart({ series, labels, band, incompleteFrom, markers, height = 220, ariaLabel, format = fmtInt }: Props) {
  const [hover, setHover] = useState<number | null>(null)
  const n = Math.max(labels.length, ...series.map((s) => s.values.length), 0)
  const plotW = W - LEFT - RIGHT
  const plotH = height - TOP - BOTTOM
  const all = [...series.flatMap((s) => s.values), ...(band ? [...band.low, ...band.high] : [])]
    .filter((v): v is number => v !== null && v !== undefined)
  const peak = Math.max(0, ...all)
  // Shares (0 to 1) need a fractional axis; whole-number axes round their ticks and drop repeats.
  const fractional = peak > 0 && peak <= 1 && all.some((v) => !Number.isInteger(v))
  const max = fractional ? niceMax(peak) : Math.max(2, niceMax(peak))
  const gridTicks = fractional ? [0, max / 2, max] : [...new Set(ticks(max, 4))]
  const yAt = (v: number) => TOP + plotH - (Math.min(v, max) / max) * plotH
  const step = Math.max(1, Math.ceil(n / 6))
  let neutral = 0
  const colours = series.map((s) => (s.emphasis ? ACCENT : INK[neutral++ % INK.length]))

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const box = e.currentTarget.getBoundingClientRect()
    if (box.width <= 0 || n === 0) return
    const frac = Math.min(Math.max((e.clientX - box.left) / box.width, 0), 1)
    setHover(n <= 1 ? 0 : Math.round(frac * (n - 1)))
  }

  const hoverX = hover === null ? null : LEFT + xForDay(hover, n, plotW)
  const tipLeft = hoverX === null ? 0 : (hoverX / W) * 100

  return (
    <div>
      <div className="relative">
        <svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: 'block', touchAction: 'pan-y' }}>
          {gridTicks.map((t) => (
            <g key={t}>
              <line x1={LEFT} x2={W - RIGHT} y1={yAt(t)} y2={yAt(t)} stroke={GRID} strokeWidth={1} />
              <text x={LEFT - 6} y={yAt(t) + 3} textAnchor="end" fontSize={10} fontFamily="ui-monospace, monospace" fill={TEXT}>
                {format(t)}
              </text>
            </g>
          ))}
          <g transform={`translate(${LEFT},${TOP})`}>
            {band && <path d={bandPath(band.low, band.high, plotW, plotH, max)} fill="#837e73" fillOpacity={0.18} stroke="none" />}
            {series.map((s, k) => {
              const { solid, dotted } = splitIncomplete(s.values, incompleteFrom)
              const stroke = colours[k]
              const width = s.emphasis ? 2 : 1.25
              return (
                <g key={s.label} fill="none" stroke={stroke} strokeWidth={width} strokeLinejoin="round">
                  <path d={linePath(solid, n, plotW, plotH, max)} strokeDasharray={s.dashed ? '5 4' : undefined} />
                  {dotted.length > 0 && <path d={linePath(dotted, n, plotW, plotH, max)} strokeDasharray="2 3" />}
                </g>
              )
            })}
            {(markers ?? []).map((m) => (
              <g key={`${m.index}-${m.label}`}>
                <line x1={markerX(m.index, n, plotW)} x2={markerX(m.index, n, plotW)} y1={0} y2={plotH} stroke={TEXT} strokeWidth={1} strokeDasharray="1 3" />
                <text x={markerX(m.index, n, plotW)} y={-3} textAnchor="middle" fontSize={9} fontFamily="ui-monospace, monospace" fill={TEXT}>{m.label}</text>
              </g>
            ))}
            {hover !== null && (
              <g pointerEvents="none">
                <line x1={xForDay(hover, n, plotW)} x2={xForDay(hover, n, plotW)} y1={0} y2={plotH} stroke={TEXT} strokeWidth={1} />
                {series.map((s, k) => {
                  const v = s.values[hover]
                  return v === null || v === undefined ? null : (
                    <circle key={s.label} cx={xForDay(hover, n, plotW)} cy={yAt(v) - TOP} r={3} fill={colours[k]} />
                  )
                })}
              </g>
            )}
            <rect x={0} y={0} width={plotW} height={plotH} fill="transparent" onPointerMove={onMove} onPointerDown={onMove} onPointerLeave={() => setHover(null)} />
          </g>
          {labels.map((l, i) =>
            i % step === 0 ? (
              <text key={`${l}-${i}`} x={LEFT + xForDay(i, n, plotW)} y={height - 6} textAnchor="middle" fontSize={10} fontFamily="ui-monospace, monospace" fill={TEXT}>
                {l}
              </text>
            ) : null,
          )}
        </svg>
        {hover !== null && (
          <div
            className="pointer-events-none absolute top-0 z-10 border border-ink-500 bg-ink-800 px-2 py-1 font-mono text-[11px] text-paper"
            style={{ left: `${tipLeft}%`, transform: tipLeft > 60 ? 'translateX(-105%)' : 'translateX(8px)' }}
          >
            <div className="text-paper-faint">{labels[hover]}</div>
            {series.map((s, k) => (
              <div key={s.label} className="flex items-center gap-2 whitespace-nowrap">
                <span aria-hidden="true" style={{ width: 8, height: 2, background: colours[k], display: 'inline-block' }} />
                <span className="text-paper-dim">{s.label}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.values[hover] === null || s.values[hover] === undefined ? '—' : format(s.values[hover] as number)}</span>
              </div>
            ))}
            {incompleteFrom !== undefined && hover >= incompleteFrom && <div className="text-paper-faint">Not a finished day</div>}
          </div>
        )}
      </div>
      {series.length > 1 && (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-paper-dim" aria-label="Legend">
          {series.map((s, k) => (
            <li key={s.label} className="flex items-center gap-2">
              <svg width={18} height={4} aria-hidden="true">
                <line x1={0} x2={18} y1={2} y2={2} stroke={colours[k]} strokeWidth={s.emphasis ? 2 : 1.25} strokeDasharray={s.dashed ? '5 4' : undefined} />
              </svg>
              {s.label}
            </li>
          ))}
          {band && <li className="flex items-center gap-2"><span aria-hidden="true" style={{ width: 18, height: 8, background: '#837e73', opacity: 0.35, display: 'inline-block' }} />Normal range</li>}
        </ul>
      )}
      <details className="mt-2 font-mono text-[11px] text-paper-dim">
        <summary className="cursor-pointer text-paper-faint">Show the numbers</summary>
        <div className="mt-1 max-h-64 overflow-auto">
          <table className="min-w-full text-left">
            <thead>
              <tr className="text-paper-faint">
                <th className="pr-4 font-normal">Day</th>
                {series.map((s) => <th key={s.label} className="pr-4 font-normal">{s.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {labels.map((l, i) => (
                <tr key={`${l}-${i}`}>
                  <td className="pr-4">{l}</td>
                  {series.map((s) => (
                    <td key={s.label} className="pr-4" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {s.values[i] === null || s.values[i] === undefined ? '—' : format(s.values[i] as number)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}
